import {
  BookOpen,
  FileSearch,
  FileText,
  MessagesSquare,
  PenLine,
  Scale,
  type LucideIcon,
} from "lucide-react";

export interface DashboardTile {
  id: string;
  title: string;
  subtitle: string;
  icon: LucideIcon;
  /** When set, tile is hidden if user lacks app permission. */
  appId?: string;
  /** Route for existing apps or coming-soon slugs. */
  to: string;
}

export interface DashboardGroup {
  id: string;
  label: string;
  tiles: DashboardTile[];
  /** Mockup grid: narrow first column (Research), wide for two-tile groups. */
  layout?: "narrow" | "wide";
}

export const DASHBOARD_GROUPS: DashboardGroup[] = [
  {
    id: "research-agents",
    label: "Research Agents",
    layout: "narrow",
    tiles: [
      {
        id: "discovery",
        title: "Legal Research Support",
        subtitle: "Research statutes, case law, and precedents with AI support.",
        icon: FileSearch,
        appId: "research",
        to: "/research",
      },
    ],
  },
  {
    id: "contracting-agents",
    label: "Contracting Agents",
    tiles: [
      {
        id: "review",
        title: "Legal Contract Review",
        subtitle: "Upload contracts, run AI risk analysis, and collaborate on review reports.",
        icon: FileText,
        appId: "contract-review",
        to: "/contract-review",
      },
      {
        id: "draft",
        title: "Legal Drafting Workspace",
        subtitle: "Draft and refine legal documents with AI-assisted clause suggestions.",
        icon: PenLine,
        appId: "drafting",
        to: "/drafting",
      },
    ],
  },
  {
    id: "assistant",
    label: "Assistant",
    tiles: [
      {
        id: "chat",
        title: "Chat",
        subtitle: "Create and manage contract types and custom roles from one conversation.",
        icon: MessagesSquare,
        appId: "chat",
        to: "/chat",
      },
    ],
  },
  {
    id: "document-management",
    label: "Document management",
    tiles: [
      {
        id: "regulatory",
        title: "Regulatory",
        subtitle: "Public legal and regulatory knowledge curated by Doculegis administrators.",
        icon: Scale,
        appId: "regulatory",
        to: "/regulatory",
      },
      {
        id: "knowledge",
        title: "KM",
        subtitle: "Query your knowledge base and get answers from your documents.",
        icon: BookOpen,
        appId: "rag",
        to: "/rag",
      },
    ],
  },
];

export const COMING_SOON_TILES: Record<
  string,
  { title: string; description: string }
> = {
  "multi-review": {
    title: "Multi review",
    description: "Due diligence workflows across multiple documents and parties.",
  },
  workflows: {
    title: "Workflows",
    description: "End-to-end legal automation for your team.",
  },
  "custom-agents": {
    title: "Custom Agents",
    description: "Tailor AI agents to your way of working.",
  },
  playbooks: {
    title: "Playbooks",
    description: "Consistent contract review, every time.",
  },
  documents: {
    title: "Documents",
    description: "Intelligent document repository for your organization.",
  },
};

export function filterVisibleTiles(
  tiles: DashboardTile[],
  hasPermission: (resource: string, action: string) => boolean
): DashboardTile[] {
  return tiles.filter((tile) => {
    if (!tile.appId) return true;
    return hasPermission("app", tile.appId);
  });
}

export function filterVisibleGroups(
  groups: DashboardGroup[],
  hasPermission: (resource: string, action: string) => boolean
): DashboardGroup[] {
  return groups
    .map((group) => ({
      ...group,
      tiles: filterVisibleTiles(group.tiles, hasPermission),
    }))
    .filter((group) => group.tiles.length > 0);
}
